import { useNewCode } from '@calendly/feature-check-react'
import { AiNotetakerDuotoneIcon, AiNotetakerNoRecapDuotoneIcon } from '@calendly/ui/components/icons'

import { useFetchTrackedMeetingWithSummaryQuery } from 'hooks/tracked_meetings/api'

import { FEATURE_FLAGS } from '../constants'
import { LinkBadge } from '../linkBadge'
import { MeetingBadge } from '../meetingBadge/MeetingBadge'

/**
 * RecapBadge component displays a recap link in a badge format.
 *
 * This component displays a recap title in a LinkBadge component with an AI notetaker icon.
 * On hover, it shows a RecapHoverCard with detailed meeting information.
 *
 * @param {Object} props - Component props
 * @param {string} [props.recapId] - The ID of the recap for navigation
 * @param {string} [props.title] - Fallback title if no recap data is available
 * @param {boolean} [props.disabled] - Whether the badge is disabled
 * @param {Object} [props.analyticsHandlers] - Analytics handlers for tracking events
 *
 * @returns {JSX.Element} A badge component showing recap icon and title
 *
 * @example
 * ```jsx
 * <RecapBadge
 *   recapId="abc123"
 *   title="Meeting Recap"
 *   analyticsHandlers={analyticsHandlers}
 * />
 *
 * <RecapBadge
 *   title="No Recap Available"
 *   disabled
 * />
 * ```
 */

export const RecapBadge = ({ recapId, title, disabled, analyticsHandlers }) => {
  const isHoverCardEnabled = useNewCode(FEATURE_FLAGS.MEETING_HOVER_CARD)?.enabled

  const { data: recapData, isLoading: isRecapLoading } = useFetchTrackedMeetingWithSummaryQuery(
    { meetingUuid: recapId },
    { skip: !recapId || disabled }
  )

  // If hover card is enabled and we have the meeting ID, use MeetingBadge instead
  // Intentially we use the meeting badge with calendar icon instead of the recap icon
  if (isHoverCardEnabled && recapData?.foreignEventId) {
    return (
      <MeetingBadge
        meetingId={recapData.foreignEventId}
        title={recapData.name || title}
        disabled={disabled}
        analyticsHandlers={analyticsHandlers}
      />
    )
  }

  // Fallback: render simple badge without hover card
  const onClick = () => {
    if (recapId) {
      if (typeof analyticsHandlers?.trackNavigationLinkInChatClicked === 'function') {
        analyticsHandlers.trackNavigationLinkInChatClicked('recap_link')
      }
      window.open(`/app/tracked_meetings/${recapId}`, '_blank', 'noopener,noreferrer')
    }
  }

  const icon = disabled ? <AiNotetakerNoRecapDuotoneIcon /> : <AiNotetakerDuotoneIcon />

  return (
    <LinkBadge
      text={recapData?.name || title}
      leftElement={icon}
      onClick={onClick}
      disabled={disabled}
      isLoading={isRecapLoading}
    />
  )
}
